import { notifySimple } from "providers/toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import GenericDialog from "./generic";
import ScheduleForm, { ScheduleFormType } from "widgets/forms/reports/schedule";
import { DeviceListReqBody } from "api/types/device";
import { deviceList } from "api/device";
import { useCallback, useEffect, useState } from "react";
import GenericButton from "widgets/controls/buttons/generic";
import { SearchInput } from "widgets/shared/inputs/search";
import { reportScheduledUpdate } from "api/report";
import { ScheduledReqBody } from "api/types/report";
import BounceLoader from "react-spinners/ClipLoader";
import {
  convertUTCToLocalTimeWithDayShift,
  shiftDowByDayDelta,
  shiftDomByDayDelta,
} from "util/datelib";

const ScheduleRecipientsDialog = (props: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  context?: "new" | "edit" | "view";
  values?: any;
}) => {
  const { isOpen, onClose, onSuccess, context = "new", values } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [devList, setDevList] = useState([]);
  const [search, setSearch] = useState("");
  const [isDevLoading, setIsDevLoading] = useState(false);

  // Function for update
  const doUpdate = async (formData: ScheduleFormType) => {
    dispatch(setLoading(true));

    const body: ScheduledReqBody = {
      id: values?._id,
      siteId: values?.config.body.siteId,
      reportType: values?.config.type,
      description: formData.description,
      config: values?.config,
      frequency: formData.frequency,
      dom: formData.dom ?? 1,
      dow: formData.dow ?? 1,
      hour: formData.hourOn.hour,
      recipients: formData.recipients.map((recipient: any) => recipient.email),
      intergrations: {},
    };
    const { status, isLoading } = await reportScheduledUpdate(body);
    dispatch(setLoading(isLoading));
    if (status && status === 200) {
      onSuccess();
      notifySimple(t("notification.report.scheduled.success"), "success");
    } else {
      notifySimple(t("notification.report.scheduled.error"), "error");
    }
  };

  const fetchDeviceList = useCallback(async (siteId: string) => {
    if (!siteId) return;

    const body: DeviceListReqBody = {
      classId: siteId,
      sort: "snr",
      desc: true,
      installed: "INSTALLED",
      models: values?.config.body.models,
    };
    setIsDevLoading(true);
    const { data, isLoading } = await deviceList(body);
    setIsDevLoading(isLoading);

    if (data) {
      setDevList(data);
    }
  }, [values?.config.body.models]);

  useEffect(() => {
    if (
      context === "view" &&
      (values?.config.body.siteId || values?.config.siteId)
    ) {
      fetchDeviceList(values?.config.body.siteId || values?.config.siteId);
    }
  }, [context, fetchDeviceList, values?.config.siteId, values?.config.body.siteId]);

  const selectedSnrs: string[] =
    values?.config?.body?.snrs || values?.config?.snrs || [];
  const baseList =
    Array.isArray(selectedSnrs) && selectedSnrs.length > 0
      ? devList.filter((dev) => selectedSnrs.includes(dev.snr))
      : devList;

  const filteredDevList = !search
    ? baseList
    : baseList.filter(
        (dev) =>
          (dev.name || "").toLowerCase().includes(search.toLowerCase()) ||
          String(dev.snr || "")
            .toLowerCase()
            .includes(search.toLowerCase())
      );

  return (
    <GenericDialog
      size="lg"
      isOpen={isOpen}
      onExit={onClose}
      title={t("dialog.schedule.recipients.title", { context })}
    >
      {context === "view" ? (
        <div className="-mt-6 flex flex-col gap-2">
          <div className="text-txt-lg">
            {t("dialog.schedule.recipients.description", { context })}
          </div>
          {isDevLoading ? (
            <div className="flex h-44 items-center justify-center">
              <BounceLoader loading={isDevLoading} />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-5">
                {(values?.config.body.snr || values?.config.snr) && (
                  <div className="text-txt-lg">
                    {(() => {
                      const device = devList.find(
                        (dev) =>
                          dev.snr ===
                          (values?.config.body.snr ||
                            dev.snr === values?.config.snr)
                      );
                      return device
                        ? `${device.name} - ${device.snr}`
                        : "Device";
                    })()}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <SearchInput value={search} onChange={setSearch} />
                <div className="grid max-h-52 grid-cols-1 gap-2 overflow-y-auto p-2">
                  {filteredDevList.map((dev) => (
                    <div key={dev.snr} className="truncate text-txt-md">
                      {dev.name ? `${dev.name} - ${dev.snr}` : "Device"}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <div className="flex gap-2">
                  <GenericButton
                    onClick={onClose}
                    text={t("button.action.ok")}
                    disabled={false}
                    size="md"
                  />
                </div>
              </div>{" "}
            </>
          )}
        </div>
      ) : (
        <div className="-mt-6 flex flex-col gap-8">
          <div className="text-txt-lg">
            {t("dialog.schedule.recipients.description", { context })}
          </div>
          <ScheduleForm
            submit={doUpdate}
            onClose={onClose}
            context={context}
            values={{
              description: values?.description || "",
              frequency: (["DAILY", "WEEKLY", "MONTHLY"] as const).includes(
                values?.frequency as any
              )
                ? (values?.frequency as "DAILY" | "WEEKLY" | "MONTHLY")
                : "DAILY",
              dom: (() => {
                const utcDom = values?.dom;
                if (utcDom == null) return null;
                const utcHour = values?.hour;
                if (utcHour == null) return utcDom;

                const local = convertUTCToLocalTimeWithDayShift(Number(utcHour));
                return shiftDomByDayDelta(Number(utcDom), local.dayDelta);
              })(),
              dow: (() => {
                const utcDow = values?.dow;
                if (utcDow == null) return null;
                const utcHour = values?.hour;
                if (utcHour == null) return utcDow;

                const local = convertUTCToLocalTimeWithDayShift(Number(utcHour));
                return shiftDowByDayDelta(Number(utcDow), local.dayDelta);
              })(),
              hourOn: (() => {
                const utcHour = values?.hour;
                if (utcHour == null) return { hour: 8, minute: 0 };
                const local = convertUTCToLocalTimeWithDayShift(Number(utcHour));
                return { hour: local.hour, minute: local.minute };
              })(),
              recipients: values?.recipients
                ? values.recipients.map((recipient: string) => ({
                    email: recipient,
                  }))
                : [],
            }}
          />
        </div>
      )}
    </GenericDialog>
  );
};

export default ScheduleRecipientsDialog;
